import React, { useState, useRef, useEffect } from 'react';
import uploadicon from '../logos/Upload.png';
import fileIcon from '../logos/pdf.png';
import moreIcon from '../logos/more.png';
import deleteIcon from '../logos/delete.png';
import viewIcon from '../logos/eye.png';
import Spinner from '../ui-components/loader';
import LoginPopup from '../ui-components/LoginPopup';
import { apiUrl } from '@/constants/constants';

interface FileItem {
  name: string;
  last_modified: string;
  size: number;
  url: string;
  upload_time: string;
  filename: string;
}

interface SasResponse {
  status: string;
  sasToken: string;
  accountUrl: string;
  documentUrl: string | null;
  expiresOn: string;
  permissions: {
    read: boolean;
    list: boolean;
  }
}

// const apiUrl = import.meta.env.VITE_API_URL;

const UploadFiles = () => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [files, setFiles] = useState<FileItem[]>([]);
  const [activeMenu, setActiveMenu] = useState<number | null>(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isLoginPopupOpen, setIsLoginPopupOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    const stored = sessionStorage.getItem('isAuthenticated');
    return stored ? JSON.parse(stored) : false;
  });
  const [pendingAction, setPendingAction] = useState<'upload' | 'more' | null>(null);
  const [pendingIndex, setPendingIndex] = useState<number | null>(null);

  const resetFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const uploadFileToServer = async (file: File) => {
    try {
      setIsUploading(true);
      const formData = new FormData();
      const processedFile = new File([file], file.name.replace(/\s+/g, '_'), { type: file.type });
      formData.append('file', processedFile);
      formData.append('tenant_id', 'default');
      formData.append('embedding_model_id', 'all-mpnet-base-v2');
      
      console.log('Uploading to:', `${apiUrl}/api/test-vectorize-file`);
      console.log('FormData:', {
        file: processedFile.name,
        tenant_id: 'default',
        embedding_model_id: 'all-mpnet-base-v2'
      });

      const response = await fetch( apiUrl + `/api/test-vectorize-file`, {
        method: 'POST',
        headers: {
          'Accept': 'application/json',
        },
        body: formData,
      });
      
      console.log('Upload Response:', {
        status: response.status,
        statusText: response.statusText
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error('Error response:', errorData);
        throw new Error(`Upload failed (${response.status}): ${errorData || response.statusText}`);
      }
      
      const data = await response.json();
      console.log('Upload success:', data);
      return data;
    } catch (error) {
      console.error('Error uploading file:', error);
      throw error;
    } finally {
      setIsUploading(false);
    }
  };

  useEffect(() => {
    let isSubscribed = true;
    const fetchFiles = async () => {
      try {
        setIsLoading(true);
        const response = await fetch( apiUrl + `/api/list-blob-files`);
        console.log('Initial List API Response:', response);
        if (!response.ok) {
          const errorData = await response.text();
          console.error('Error response:', errorData);
          throw new Error(`Failed to fetch files: ${errorData}`);
        }

        const data = await response.json();
        console.log('Files data:', data);

        if (isSubscribed) {
          if (Array.isArray(data)) {
            setFiles(data);
          } else if (data && Array.isArray(data.files)) {
            setFiles(data.files || []);
          } else {
            console.error('Unexpected data format:', data);
            setFiles([]);
          }
        }
      } catch (error) {
        console.error('Error fetching files:', error);
        if (isSubscribed) {
          setFiles([]);
        }
      } finally {
        if (isSubscribed) {
          setIsLoading(false);
        }
      }
    };

    fetchFiles();

    return () => {
      isSubscribed = false;
    };
  }, []);

  const handleLoginSuccess = () => {
    setIsAuthenticated(true);
    sessionStorage.setItem('isAuthenticated', 'true');
    setIsLoginPopupOpen(false);
    
    // Execute pending action
    if (pendingAction === 'upload') {
      handleUploadClick();
    } else if (pendingAction === 'more' && pendingIndex !== null) {
      handleMoreClick(pendingIndex);
    }
    
    setPendingAction(null);
    setPendingIndex(null);
  };

  const handleMoreClick = (index: number) => {
    if (!isAuthenticated) {
      setPendingAction('more');
      setPendingIndex(index);
      setIsLoginPopupOpen(true);
      return;
    }

    setActiveMenu(index);
  };

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    resetFileInput();
    
    if (file) {
      try {
        setIsUploading(true);
        await uploadFileToServer(file);
        const response = await fetch( apiUrl + `/api/list-blob-files`);
        console.log('List Response:', response);
        if (!response.ok) {
          throw new Error('Failed to fetch files');
        }
        const data = await response.json();
        console.log('List Data:', data);
        if (Array.isArray(data)) {
          const transformedFiles = data.map((file: any) => ({
            name: file.name,
            last_modified: file.last_modified,
            size: file.size,
            url: file.url,
            upload_time: file.last_modified,
            filename: file.name
          }));
          setFiles(transformedFiles);
        } else {
          console.error('Unexpected data format:', data);
          setFiles([]);
        }
      } catch (error) {
        console.error('Upload failed:', error);
        setFiles([]);
      } finally {
        setIsUploading(false);
      }
    }
  };

  const handleUploadClick = () => {
    if (!isAuthenticated) {
      setPendingAction('upload');
      setIsLoginPopupOpen(true);
      return;
    }

    if (!isUploading && fileInputRef.current) {
      resetFileInput();
      fileInputRef.current.click();
    }
  };

  const handleViewDocument = async (fileUrl: string) => {
    try {
      // Remove the @ symbol if it exists at the start of the URL
      const cleanFileUrl = fileUrl.startsWith('@') ? fileUrl.substring(1) : fileUrl;
      
      // Request SAS token with the blob URL
      const response = await fetch( apiUrl + `/api/generate_sas_token?blob_url=${encodeURIComponent(cleanFileUrl)}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        throw new Error('Failed to get SAS token');
      }

      const data: SasResponse = await response.json();
      
      if (data.status === 'success' && data.documentUrl) {
        // Open the document using the full URL with SAS token
        window.open(data.documentUrl, '_blank');
      } else {
        throw new Error('Failed to get document URL');
      }
      
      setActiveMenu(null);
    } catch (error) {
      console.error('Failed to view document:', error);
    }
  };

  const handleDelete = async (fileUrl: string) => {
    try {
      setIsUploading(true);
      const params = new URLSearchParams({ blob_name: fileUrl });
      const response = await fetch( apiUrl +`/api/delete_document?${params.toString()}`, {
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      if (!response.ok) {
        const errorData = await response.text();
        console.error('Delete error response:', errorData);
        setIsUploading(false);
        throw new Error(`Delete failed: ${errorData}`);
      }

      // Update the files list after successful deletion
      setFiles(files.filter(file => file.url !== fileUrl));
      setActiveMenu(null); // Close the menu after deletion
      setIsUploading(false);
    } catch (error) {
      console.error('Delete failed:', error);
      setIsUploading(false);
    }
  };

  console.log('isAuthenticated', isAuthenticated);
  

  return (
    <div className="flex-grow flex flex-col bg-[#f2f4f9] overflow-hidden h-screen">
      {isUploading && (
        <Spinner message="Processing your request..." />
      )}
      <div className="bg-white my-8 mr-8 rounded-[20px] h-[calc(100vh-4rem)] flex flex-col">
        <div className="p-8">
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            className="hidden"
            accept=".pdf,.doc,.docx"
          />
          <div className="flex justify-between items-center mb-6">
            <h2 className="font-montserrat ml-8 text-[16px] font-semibold">Knowledge Base</h2>
            <button 
              className={`flex items-center gap-2 py-2 px-4 mr-16 ${
                isUploading ? 'bg-black' : 'bg-black'
              } w-[125px] rounded-[20px] cursor-pointer text-sm text-white font-medium`}
              onClick={handleUploadClick}
              disabled={isUploading}
            >
              <img src={uploadicon} alt="Upload" className="w-6 h-6 object-contain" />
              { !isAuthenticated ? 'Admin' : isUploading ? 'Uploading' : 'Upload' }
            </button>
          </div>

          <div className="mt-4 overflow-y-auto h-[calc(100vh-12rem)]">
            {isLoading ? (
              <div className="flex justify-center items-center h-32">
                <span className="text-gray-500">Loading files...</span>
              </div>
            ) : !files || files.length === 0 ? (
              <div className="flex justify-center items-center h-32">
                <span className="text-gray-500">No files uploaded yet</span>
              </div>
            ) : (
              files.map((file, index) => (
                <div 
                  key={index}
                  className="w-full flex items-center justify-between py-4 px-8 border-b border-[#F2F4F9] hover:bg-gray-50 relative"
                >
                  <div className="flex items-center gap-3 w-[70%]">
                    <img src={fileIcon} alt="File" className="w-3.4 h-3.5 flex-shrink-0" />
                    <span className="font-montserrat text-[12px] text-sm text-[#4B4B4B] truncate">
                      {file.name}
                    </span>
                  </div>
                  
                  <div className="flex items-center gap-4 w-[30%] justify-end">
                    <span className="font-montserrat text-sm text-[12px] text-[#808080] truncate">
                      {new Date(file.last_modified).toLocaleString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        hour: 'numeric',
                        minute: 'numeric',
                        hour12: true
                      })}
                    </span>
                    <button 
                      className="p-2 hover:bg-gray-100 rounded-full flex-shrink-0"
                      onClick={() => handleMoreClick(index)}
                    >
                      <img src={moreIcon} alt="More" className="w-4 h-1.3" />
                    </button>

                    {activeMenu === index && (
                      <div className="absolute right-8 top-12 w-48 bg-white rounded-[12px] shadow-lg border border-[#F2F4F9] z-10">
                        <button className="w-full text-left px-4 py-3 text-sm text-[#4B4B4B] hover:bg-[#F2F4F9] flex items-center gap-3" onClick={() => handleViewDocument(file.url)}>
                          <img src={viewIcon} alt="File" className="w-4 h-4" />
                          <span>View</span>
                        </button>
                        <button 
                          className="w-full text-left px-4 py-3 text-sm text-[#4B4B4B] hover:bg-[#F2F4F9] flex items-center gap-3"
                          onClick={() => handleDelete(file.url)}
                        >
                          <img src={deleteIcon} alt="File" className="w-3 h-4" />
                          <span>Delete</span>
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Login Popup */}
      <LoginPopup 
        isOpen={isLoginPopupOpen}
        onClose={() => {
          setIsLoginPopupOpen(false);
          setPendingAction(null);
          setPendingIndex(null);
        }}
        onSuccess={handleLoginSuccess}
      />
    </div>
  );
};

export default UploadFiles; 